import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// Darken a hex colour by a factor (0-1). Used to derive gradient/hover shades.
function shade(hex, factor) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return hex;
  const r = Math.round(parseInt(m[1], 16) * factor);
  const g = Math.round(parseInt(m[2], 16) * factor);
  const b = Math.round(parseInt(m[3], 16) * factor);
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')}`;
}

const FALLBACK = {
  Cardo: "'Times New Roman', serif",
  'PT Serif': 'Georgia, serif',
};

// Applies live theme (colours, fonts, dynamic Google Font loading) to :root.
export default function ThemeApplier() {
  const { settings } = useAuth();

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    const c = settings.colors || {};
    const set = (k, v) => v && root.style.setProperty(k, v);

    set('--maroon', c.maroon);
    set('--maroon-dark', shade(c.maroon, 0.8));
    set('--navy', c.navy);
    set('--navy-dark', shade(c.navy, 0.75));
    set('--sage', c.sage);
    set('--sage-light', shade(c.sage, 1.08));
    set('--olive', c.olive);
    set('--ink', c.ink);
    set('--cream', c.cream);
    set('--gold', c.gold);

    const fonts = settings.fonts || {};
    if (fonts.title) root.style.setProperty('--font-title', `'${fonts.title}', ${FALLBACK[fonts.title] || 'serif'}`);
    if (fonts.body) root.style.setProperty('--font-body', `'${fonts.body}', ${FALLBACK[fonts.body] || 'sans-serif'}`);

    // Load selected Google fonts dynamically
    const families = [fonts.title, fonts.body].filter(Boolean);
    if (families.length) {
      const href = 'https://fonts.googleapis.com/css2?' +
        families.map((f) => `family=${encodeURIComponent(f)}:wght@400;700`).join('&') +
        '&display=swap';
      let link = document.getElementById('dynamic-fonts');
      if (!link) {
        link = document.createElement('link');
        link.id = 'dynamic-fonts';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      if (link.href !== href) link.href = href;
    }
  }, [settings]);

  return null;
}
